import React, { useState, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Icon, Button, Box, Flex, Text
} from 'components/ui';
import getSkuDetails from 'services/api/search-n-browse/getSkuDetails';
import { addToCart } from 'services/api/basket/addToCartAgent';
import { radii } from 'style/config';

function Search({ refreshBasketClick }) {
    const [sku, setSku] = useState({});
    const [isFocused, setIsFocused] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const formContainerRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = event => {
            if (formContainerRef.current && !formContainerRef.current.contains(event.target)) {
                setIsFocused(false);
            }
        };

        const injectSpinnerStyles = () => {
            // CSS animation for spinner
            const spinnerKeyframes = `
                @keyframes agent-spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `;

            // Inject keyframes if not already present
            if (!document.getElementById('agent-spinner-styles')) {
                const style = document.createElement('style');

                style.id = 'agent-spinner-styles';
                style.innerHTML = spinnerKeyframes;
                document.head.appendChild(style);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        injectSpinnerStyles();

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSearchChange = event => {
        handleResetSearch();
        setSearchValue(event.target.value.split(' ').join(''));
    };

    const handleSearchSubmit = event => {
        if (event.key === 'Enter') {
            setIsError(false);
            setSku({}); // Clear previous results

            if (searchValue.length > 0) {
                setIsLoading(true);

                getSkuDetails(searchValue)
                    .then(data => {
                        setIsLoading(false);
                        setSku(data);
                    })
                    .catch(() => {
                        setIsLoading(false);
                        setIsError(true);
                    });
            }
        }
    };

    const handleResetSearch = () => {
        setSearchValue('');
        setSku({});
        setIsError(false);

        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const addProductsToCartAndRefreshBasket = () => {
        // Transform sku data to match addToCart API expectations

        addToCart(sku)
            .then(() => {
                if (refreshBasketClick) {
                    refreshBasketClick();
                }

                setSearchValue('');
                setSku({});
                setIsFocused(false);
            })
            .catch(() => {
                // Could add error handling here
            });
    };

    const renderError = () => {
        return (
            <Flex
                flexDirection='column'
                css={{
                    justifyContent: 'center',
                    minHeight: '114px'
                }}
            >
                <Text
                    fontWeight='bold'
                    children='No item found'
                />
                <Text children='The item number does not exist. Please check the number and try again.' />
            </Flex>
        );
    };

    const renderResult = () => {
        if (isLoading) {
            return (
                <Flex
                    flexDirection='column'
                    alignItems='center'
                    css={{
                        justifyContent: 'center',
                        padding: '30px 20px'
                    }}
                >
                    <Box
                        css={{
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #000',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            WebkitAnimation: 'agent-spin 1s linear infinite',
                            animation: 'agent-spin 1s linear infinite'
                        }}
                    />
                </Flex>
            );
        }

        return (
            <Flex
                gap='16px'
                width='100%'
                justifyContent='space-between'
            >
                <Flex
                    alignItems='flex-start'
                    gap='16px'
                >
                    <Box
                        is='img'
                        width='50px'
                        height='50px'
                        src={sku.skuImages?.image42 || sku.skuImages?.image250 || ''}
                        alt={sku.primaryProduct?.brand?.displayName}
                    />
                    <Flex flexDirection='column'>
                        <Text
                            fontWeight='bold'
                            children={sku.primaryProduct?.brand?.displayName}
                        />
                        <Text children={sku.primaryProduct?.displayName} />
                        <Text children={sku.variationType === 'Color' ? `Color: ${sku.variationValue}` : ''} />
                        <Text children={sku.size ? `Size: ${sku.size}` : ''} />
                        <Text
                            fontWeight='bold'
                            children={sku.listPrice}
                        />
                    </Flex>
                </Flex>
                {sku.skuId && (
                    <Button
                        variant='primary'
                        size='sm'
                        onClick={() => addProductsToCartAndRefreshBasket()}
                        title={`${sku.primaryProduct?.displayName} ${sku.isOutOfStock ? 'is Out of Stock' : 'Add to basket'}`}
                        disabled={!!sku.isOutOfStock}
                        css={{
                            width: '120px',
                            height: '32px',
                            borderRadius: radii.full,
                            border: sku.isOutOfStock ? 'none' : '2px solid #000000',
                            cursor: sku.isOutOfStock ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {sku.isOutOfStock ? 'Out of Stock' : 'Add'}
                    </Button>
                )}
            </Flex>
        );
    };

    const renderSearchResults = () => {
        if (!isFocused || (!sku.skuId && !isError && !isLoading)) {
            return null;
        }

        return (
            <Box
                is='ul'
                id='agent-aware-search-listbox'
                aria-labelledby='agent-aware-search-label'
                role='listbox'
                position='absolute'
                width='100%'
                css={{
                    zIndex: '1',
                    borderRadius: '4px',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.2)'
                }}
            >
                <Box
                    is='li'
                    role='option'
                    id='agent-aware-search-result'
                    display='flex'
                    alignItems='flex-start'
                    css={{
                        padding: '16px 16px'
                    }}
                >
                    {isError ? renderError() : renderResult()}
                </Box>
            </Box>
        );
    };

    const searchValueIsEmpty = searchValue.length === 0;

    return (
        <Box
            className='agent-aware-hide'
            role='search'
            ref={formContainerRef}
            position='relative'
        >
            <Text
                is='label'
                id='agent-aware-search-label'
                display='none'
                children='Search by item number'
            />
            <Flex
                role='combobox'
                aria-expanded={!!sku.skuId && isFocused}
                aria-owns='agent-aware-search-listbox'
                aria-haspopup='listbox'
                id='agent-aware-search-combobox'
                alignItems='center'
                gap='7px'
                height='39px'
                paddingLeft='16px'
                borderRadius={radii.full}
                css={{
                    boxSizing: 'border-box',
                    border: isFocused ? '1px solid #000000' : '',
                    backgroundColor: isFocused ? '#FFFFFF' : '#F6F6F8'
                }}
            >
                {isFocused ? null : (
                    <Icon
                        name='search'
                        size={16}
                    />
                )}
                <input
                    ref={searchInputRef}
                    css={{
                        width: '95%',
                        padding: '9px 0px',
                        outline: 'none',
                        border: 'none',
                        backgroundColor: 'transparent'
                    }}
                    type='search'
                    id='search-sku-id'
                    name='search-sku-id'
                    value={searchValue}
                    aria-controls='agent-aware-search-listbox'
                    onChange={handleSearchChange}
                    onKeyDown={handleSearchSubmit}
                    placeholder='Search by item number'
                    autoComplete='off'
                    onFocus={() => setIsFocused(true)}
                />
                {isFocused && !searchValueIsEmpty ? (
                    <Button
                        variant='link'
                        title='Cancel search'
                        onClick={handleResetSearch}
                        css={{
                            display: 'flex',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            minWidth: 'auto',
                            cursor: 'pointer'
                        }}
                    >
                        <Icon
                            name='x'
                            size={16}
                        />
                    </Button>
                ) : null}
            </Flex>
            {renderSearchResults()}
        </Box>
    );
}

export default wrapFunctionalComponent(Search, 'Search');
